import xButton from './img/x-button.png';
import './multi-gallery.css'; // This is where your custom CSS is located
import './Modal.css'; // Assuming this CSS handles react-modal defaults
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faTimes, faPlayCircle } from '@fortawesome/free-solid-svg-icons';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';

import ReactPlayer from 'react-player';
import Modal from 'react-modal';
import { useSpring, animated, useTransition } from 'react-spring';

// PythonAppWrapper is now dynamically imported
// This component should internally manage its loading state and display a spinner if needed.
const PythonAppWrapper = React.lazy(() => import('./PythonAppWrapper'));

// Helper component for video consent
function VideoConsentOverlay({ onAcceptAndPlay }) {
    return (
        <div className="video-consent-overlay">
            <p>Questo video potrebbe impostare cookie di terze parti.</p>
            <button onClick={onAcceptAndPlay} className="consent-play-button">
                <FontAwesomeIcon icon={faPlayCircle} /> Riproduci video e accetta cookie
            </button>
        </div>
    );
}

// Helper for spring animation wrapper (keeping it as per your previous code)
function AddSpringAnimation({item}) {
    const props = useSpring({
        from: { opacity: 0, transform: 'translateY(-100px)' },
        to: { opacity: 1, transform: 'translateY(0px)' },
        config: { duration: 700, delay: 10 }
    });

    return (
        <animated.div id="anim-div" style={props}>
            {item}
        </animated.div>
    );
}

// Default properties for a gallery item, including new ones for web apps
const defaultItem = {
    type: 'none',
    title: '',
    description: '',
    linkClass: '',
    content: '', // For images/videos, this is the URL. For webapps, this can be a default thumbnail URL if 'image' is not provided.
    image: '', // Explicitly for the thumbnail in the grid display if different from 'content'
    extras: {
        title: '',
        url: ''
    },
    tags: [], // Default to an empty array for tags
    component: null, // The React component to render for the webapp
    backendUrl: null, // URL for Python backend if the webapp requires PythonAppWrapper
};


function extractVideoId(url) {
    if (!url) {
        return null;
    }
    const videoIdMatch = url.match(/(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?/);
    return videoIdMatch ? videoIdMatch[1] : null;
}


function organizeItems(items) {
    // Merge each item with default values and assign an index for stable keys
    return items.map((item, index) => ({ ...defaultItem, ...item, index }));
}

// MultiGallery component definition
function MultiGallery({
    items = [],
    config = {},
    // CAMBIATO: Ora riceve l'oggetto completo delle preferenze
    acceptedCookieCategories = { essential: true, analytics: false, marketing: false, externalMedia: false },
    // CAMBIATO: Funzione per aggiornare le preferenze nel genitore (specifica per externalMedia)
    onUpdateCookiePreferences = () => {} 
}) {
    // Organize items initially to ensure default properties and a stable index
    const organizedItems = useMemo(() => organizeItems(items), [items]);

    const [selectedItem, setSelectedItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    // State for specific video consent within the modal (user clicks "Play video")
    const [hasConsentedToVideoEmbed, setHasConsentedToVideoEmbed] = useState(false);    

    // Destructure config with new 'filterable' property and its default
    const { showNavigationArrows = true, overlayColor = 'rgba(0, 0, 0, 0.5)', filterable = false } = config;

    // State for filtering
    const [selectedTags, setSelectedTags] = useState([]);
    const [allTags, setAllTags] = useState([]);

    // Effect to collect all unique tags from items
    useEffect(() => {
        if (!filterable) return;

        const uniqueTags = new Set();
        organizedItems.forEach(item => {
            if (item.tags && Array.isArray(item.tags)) {
                item.tags.forEach(tag => uniqueTags.add(tag));
            }
        });
        setAllTags(Array.from(uniqueTags).sort());
    }, [organizedItems, filterable]);

    // Memoized filtered items based on selected tags
    const filteredItems = useMemo(() => {
        if (!filterable || selectedTags.length === 0) {
            return organizedItems;
        }
        return organizedItems.filter(item =>
            item.tags && selectedTags.every(tag => item.tags.includes(tag))
        );
    }, [organizedItems, selectedTags, filterable]);

    // React-spring useTransition for animating items in and out
    const transitions = useTransition(filteredItems, {
        keys: item => item.id || item.index, // Use item.id if available, otherwise fallback to index for stable keys
        from: { opacity: 0, transform: 'scale(0.8) translateY(-20px)' },
        enter: { opacity: 1, transform: 'scale(1) translateY(0px)' },
        leave: { opacity: 0, transform: 'scale(0.8) translateY(-20px)' },
        config: { mass: 1, tension: 200, friction: 20, duration: 300 },
    });

    useEffect(() => {
        Modal.setAppElement('#page'); // Ensure the root element is set for React Modal
    }, []);

    // Reset video consent when modal closes or item changes
    useEffect(() => {
        if (!isModalOpen) {
            setHasConsentedToVideoEmbed(false);
        }
    }, [isModalOpen, selectedItem]);

    // Moved handlePrevItem and handleNextItem definitions BEFORE the useEffect that uses them
    const handlePrevItem = useCallback(() => {
        if (!selectedItem || filteredItems.length === 0) return;
        const currentFilteredIndex = filteredItems.findIndex(item => item.index === selectedItem.index);
        if (currentFilteredIndex > 0) {
            setSelectedItem(filteredItems[currentFilteredIndex - 1]);
        } else {
            setSelectedItem(filteredItems[filteredItems.length - 1]);
        }
    }, [selectedItem, filteredItems]); // Dependencies here are correct

    const handleNextItem = useCallback(() => {
        if (!selectedItem || filteredItems.length === 0) return;
        const currentFilteredIndex = filteredItems.findIndex(item => item.index === selectedItem.index);
        if (currentFilteredIndex < filteredItems.length - 1) {
            setSelectedItem(filteredItems[currentFilteredIndex + 1]);
        } else {
            setSelectedItem(filteredItems[0]);
        }
    }, [selectedItem, filteredItems]); // Dependencies here are correct


    useEffect(() => {
        const handleKeyDown = (event) => {
            if (isModalOpen && showNavigationArrows) {
                if (event.key === 'ArrowLeft') {
                    handlePrevItem();    
                } else if (event.key === 'ArrowRight') {
                    handleNextItem();    
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isModalOpen, showNavigationArrows, handleNextItem, handlePrevItem]); // Dependency array should be fine now


    const handleItemClick = (item) => {
        setSelectedItem(item);
        setIsModalOpen(true);
        // CAMBIATO: Controlla direttamente acceptedCookieCategories.externalMedia
        if (['youtube', 'vimeo', 'dailymotion'].includes(item.type)) {
            if (acceptedCookieCategories.externalMedia) { // Usa la preferenza granulare
                setHasConsentedToVideoEmbed(true);
            } else {
                setHasConsentedToVideoEmbed(false);
            }
        }
    };

    const handleModalClose = useCallback(() => {
        setSelectedItem(null);
        setIsModalOpen(false);
        setHasConsentedToVideoEmbed(false);
    }, []);

    // CAMBIATO: Aggiorna solo la preferenza 'externalMedia' tramite il gestore del genitore
    const handleAcceptAndPlayVideo = useCallback(() => {
        // Chiama la funzione passata dal genitore per aggiornare le preferenze dei cookie
        onUpdateCookiePreferences({ ...acceptedCookieCategories, externalMedia: true });
        setHasConsentedToVideoEmbed(true); // Aggiorna lo stato interno per la riproduzione immediata
    }, [onUpdateCookiePreferences, acceptedCookieCategories]);


    const handleTagClick = useCallback((tag) => {
        setSelectedTags(prevTags =>
            prevTags.includes(tag)
                ? prevTags.filter(t => t !== tag)
                : [...prevTags, tag]
        );
    }, []);


    const renderModalContent = (item) => {
        if (!item) return null;

        const extraTitle = item.extras?.title;
        const extraUrl = item.extras?.url;

        const Extras_html = (
            <div id="extras">
                {extraUrl && extraTitle && (
                    <a href={extraUrl} target="_blank" rel="noopener noreferrer">
                        <h3>{extraTitle}</h3>
                    </a>
                )}
            </div>
        );

        switch (item.type) {
            case 'youtube':
            case 'vimeo':
            case 'dailymotion':
                // CAMBIATO: Controlla la preferenza granulare per externalMedia
                if (acceptedCookieCategories.externalMedia || hasConsentedToVideoEmbed) {
                    return (
                        <div className="video-content">                            
                            {item.content && <ReactPlayer 
                                url={item.content.includes('youtube.com') ? item.content.replace('youtube.com', 'youtube-nocookie.com') : item.content} 
                                controls={true} 
                                playing={true} 
                                width="100%" 
                                height="60vh"
                                className="react-player-wrapper" // <-- AGGIUNTA QUESTA CLASSE!
                            />}
                            {Extras_html}
                        </div>
                    );
                } else {
                    return (
                        <div className="video-content video-blocked">
                            <VideoConsentOverlay onAcceptAndPlay={handleAcceptAndPlayVideo} />
                        </div>
                    );
                }
            case 'image':
                return (
                    <div className="image-content">
                        {item.content && (
                            extraUrl ? (
                                <a className="image-url" href={extraUrl} target="_blank" rel="noopener noreferrer">
                                    <img src={item.content} alt={item.title} />
                                </a>
                            ) : (
                                <img src={item.content} alt={item.title} />
                            )
                        )}
                        {Extras_html}
                    </div>
                );
            case 'html':
                return (
                    <div className="html-content" dangerouslySetInnerHTML={{ __html: item.content || '' }} >
                        {Extras_html}
                    </div>
                );
            case 'webapp':    
                const AppComponent = item.component;
                const appBackendUrl = item.backendUrl; // THIS IS THE KEY: only if this exists, we use PythonAppWrapper

                if (!AppComponent) {
                    console.error('MultiGallery: Web app component not provided for item:', item);
                    return <p className="error-message">Error: Web app component missing.</p>;
                }

                // Conditional rendering:
                // If appBackendUrl is provided, wrap with PythonAppWrapper.
                // Otherwise, render the component directly (for standalone React apps).
                const appContent = appBackendUrl ? (
                    <React.Suspense fallback={
                        <div className="app-loading-fallback">
                            <div className="spinner"></div>
                            <p>Loading app and connecting to backend...</p>
                        </div>
                    }>
                        <PythonAppWrapper backendUrl={appBackendUrl}>
                            {/* AppComponent receives props for consent management directly */}
                            <AppComponent
                                // CAMBIATO: Passa l'oggetto completo delle preferenze
                                acceptedCookieCategories={acceptedCookieCategories}
                                // CAMBIATO: Passa la funzione per aggiornare le preferenze, se l'app interna ne avesse bisogno
                                onUpdateCookiePreferences={onUpdateCookiePreferences} 
                            />
                        </PythonAppWrapper>
                    </React.Suspense>
                ) : (
                    // Render standalone React component directly
                    <AppComponent
                        // CAMBIATO: Passa l'oggetto completo delle preferenze
                        acceptedCookieCategories={acceptedCookieCategories}
                        // CAMBIATO: Passa la funzione per aggiornare le preferenze, se l'app interna ne avesse bisogno
                        onUpdateCookiePreferences={onUpdateCookiePreferences} 
                    />
                );

                return (
                    <div className="webapp-content">    
                        {appContent}
                        {Extras_html}
                    </div>
                );
            default:
                return null;
        }
    };

    const renderItemContent = (item) => {
        const normalizedItem = { ...defaultItem, ...item };    

        const renderDescriptionWithNewlines = (description) => {
            if (!description) return null;
            return description.split('\n').map((part, index) => (
                <React.Fragment key={index}>
                    {part.trim()}
                    {index < description.split('\n').length - 1 && <br />}
                </React.Fragment>
            ));
        };

        switch (normalizedItem.type) {
            case 'youtube':
            case 'vimeo':
            case 'dailymotion':
                const videoId = extractVideoId(item.content);
                return (
                    <div className="gallery-item" onClick={() => handleItemClick(item)}>
                        {videoId && <img src={`https://img.youtube.com/vi/${videoId}/0.jpg`} alt={item.title} />}
                        <div className="gallery-item-caption">
                            <div>
                                <h2>{item.title}</h2>
                                <p>{renderDescriptionWithNewlines(item.description)}</p>
                            </div>
                        </div>
                    </div>
                );
            case 'image':
                return (
                    <div className="gallery-item-content" onClick={() => handleItemClick(item)} >
                        {item.content && <img src={item.content} alt={item.title} />}
                        <div className="gallery-item-caption">
                            <h2>{item.title}</h2>
                            <p>{renderDescriptionWithNewlines(item.description)}</p>
                        </div>
                    </div>
                );
            case 'html':
                return (
                    <div className="gallery-item-content" dangerouslySetInnerHTML={{ __html: item.content || '' }} onClick={() => handleItemClick(item)} />
                );
            case 'webapp':    
                return (
                    <div className="gallery-item-content webapp-preview" onClick={() => handleItemClick(item)}>
                        {/* Use 'image' prop for thumbnail in the grid, if provided, otherwise fallback to 'content' */}
                        {item.image ? (    
                            <img src={item.image} alt={`${item.title} Thumbnail`} className="w-full h-full object-cover rounded-t-lg" />
                        ) : item.content ? (    
                            <img src={item.content} alt={`${item.title} Thumbnail`} className="w-full h-full object-cover rounded-t-lg" />
                        ) : (
                            <div className="webapp-icon-placeholder">
                                <FontAwesomeIcon icon={faGlobe} />
                            </div>
                        )}
                        <div className="gallery-item-caption">
                            <h2>{item.title}</h2>
                            <p>{renderDescriptionWithNewlines(item.description)}</p>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div id="multi-gallery-content" className="content">
            {/* Filter bar, conditionally rendered */}
            {filterable && allTags.length > 0 && (
                <div className="filter-bar-container">
                    <div className="filter-tags-wrapper">
                        {<span className="filter-by-label">Filter by:</span>}
                        {allTags.map(tag => (
                            <button
                                key={tag}
                                className={`filter-button ${selectedTags.includes(tag) ? 'active' : ''}`}
                                onClick={() => handleTagClick(tag)}
                            >
                                {tag}
                            </button>
                        ))}
                    </div>
                    {selectedTags.length > 0 && (
                        <div className="clear-filters-wrapper">
                            <button
                                className="clear-filters-button"
                                onClick={() => setSelectedTags([])}
                            >
                                Clear Filters <FontAwesomeIcon icon={faTimes} className="ml-2" />
                            </button>
                        </div>
                    )}
                </div>
            )}
            
            <div className="multi-gallery">
                <div className="items">
                    {transitions((styles, item) => (
                        <animated.div className="gallery-item" style={styles}>
                            {renderItemContent(item)}
                        </animated.div>
                    ))}
                    {!filteredItems.length && filterable && selectedTags.length > 0 && (
                        <p className="no-items-message">
                            No items found matching the selected filters.
                        </p>
                    )}
                    {!filteredItems.length && !filterable && (
                        <p className="no-items-message">
                            No items available.
                        </p>
                    )}
                </div>
            </div>

            {selectedItem && (
                <Modal
                    isOpen={isModalOpen}
                    onRequestClose={handleModalClose}
                    style={{
                        overlay: {
                            backgroundColor: overlayColor,
                            zIndex: 9999,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            cursor: `url(${xButton}), auto`,
                            maxWidth: '100%',
                            margin: 'auto',
                        },
                        content: {
                            maxHeight: '95vh',
                            maxWidth: '100%',
                            width: 'clamp(min(480px, 90% ),80%, 1200px)',
                            margin: 'auto',
                            padding: '0',
                            border: 'none',
                            overflow: 'auto',
                            backgroundColor: 'transparent', /* Let .modal-content handle background */
                            zIndex: 10000,    
                        },
                    }}
                >
                    <div className="modal-content"> {/* Applied CSS classes from multi-gallery.css here */}
                        {/* X Close Button */}
                        <button
                            className="modal-close-x"
                            onClick={handleModalClose}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                        {renderModalContent(selectedItem)}

                        {/* Navigation buttons and close button outside the app content but within modal */}
                        <div className="modal-navigation">
                            {showNavigationArrows && (
                                <>
                                    <button className="navigation-button" onClick={handlePrevItem}>
                                        <FontAwesomeIcon icon={faChevronLeft} />
                                    </button>
                                    <button className="navigation-button" onClick={handleNextItem}>
                                        <FontAwesomeIcon icon={faChevronRight} />
                                    </button>
                                </>
                            )}
                        </div>
                        {/* Original "Close" button - hidden on desktop, still exists for mobile if needed for alternative closing */}
                        <button
                            className="button-close"
                            onClick={handleModalClose}
                        >
                            Close
                        </button>
                    </div>
                </Modal>
            )}
        </div>
    );
}

export default MultiGallery;
